/* eslint-disable @typescript-eslint/no-explicit-any */
import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from '@mui/joy'
import { Popover } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CheckCircle, Cancel, TrendingUp, TrendingDown } from '@mui/icons-material'
import { ClientEmail, EmailParticipant } from '@numeo/types-core'
import { useState } from 'react'

export interface EmailHeaderProps {
    from: EmailParticipant
    subtitle: string
    subtitleStyle?: Record<string, any>
    detectionResult?: ClientEmail['detectionResult']
    isExpanded?: boolean
    isFromMainSender?: boolean
    emailPreview?: string
}

export default function EmailHeader({ from, subtitle, subtitleStyle, detectionResult, isExpanded, isFromMainSender, emailPreview }: EmailHeaderProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)

    const getDetectionIcon = (isLoadOffer: boolean) => {
        if (isLoadOffer) {
            return <CheckCircle sx={{ fontSize: '1rem', color: 'success.main' }} />
        } else {
            return <Cancel sx={{ fontSize: '1rem', color: 'warning.main' }} />
        }
    }

    const getScoreIcon = (score: number) => {
        if (score > 0.7) {
            return <TrendingUp sx={{ fontSize: '0.875rem', color: 'success.main' }} />
        } else {
            return <TrendingDown sx={{ fontSize: '0.875rem', color: 'warning.main' }} />
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                width: '100%',
                cursor: 'pointer',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <Avatar
                    variant="soft"
                    color={isFromMainSender ? 'primary' : 'neutral'}
                    sx={{
                        '--Avatar-size': '40px',
                    }}
                >
                    {(from?.name || from?.email || ' ')[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography
                            level="title-md"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {from?.name || from?.email}
                        </Typography>
                        {from?.name && (
                            <Typography
                                level="body-xs"
                                sx={{
                                    color: 'text.tertiary',
                                    fontSize: '0.75rem',
                                }}
                            >
                                ({from?.email})
                            </Typography>
                        )}
                        {!isExpanded && (
                            <Typography
                                level="body-sm"
                                sx={{
                                    color: 'text.tertiary',
                                    fontSize: '0.8rem',
                                    ...subtitleStyle,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>

                    {isExpanded && (
                        <Typography
                            level="body-sm"
                            sx={{
                                color: 'text.tertiary',
                                fontSize: '0.9rem',
                                ...subtitleStyle,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}

                    {/* Show preview when collapsed */}
                    {!isExpanded && (
                        <Typography
                            level="body-sm"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                opacity: 0.8,
                                mt: 1,
                            }}
                        >
                            {emailPreview}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Enhanced Detection Results */}
            {detectionResult && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        variant="soft"
                        color={detectionResult.isLoadOffer ? 'success' : 'warning'}
                        size="md"
                        startDecorator={getDetectionIcon(detectionResult.isLoadOffer)}
                        sx={{
                            fontWeight: 600,
                            borderRadius: 'md',
                            px: 1.5,
                            minHeight: '32px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                {detectionResult.isLoadOffer ? 'Load Offer' : 'No Load'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                {getScoreIcon(detectionResult.score)}
                                <Typography level="body-xs" sx={{ fontWeight: 500 }}>
                                    {!isNaN(detectionResult.score) ? `${Math.round(detectionResult.score * 100)}%` : '0%'}
                                </Typography>
                            </Box>
                        </Box>
                    </Chip>

                    <Tooltip title="View detection details" placement="top">
                        <IconButton variant="soft" color="neutral" size="sm" onClick={handlePopoverOpen}>
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* Enhanced Popover */}
                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: 'md',
                                    boxShadow: 'lg',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                },
                            },
                        }}
                    >
                        <Box sx={{ p: 2, maxWidth: 400 }}>
                            <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                AI Detection Analysis
                            </Typography>

                            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={detectionResult.isLoadOffer ? 'success' : 'warning'}
                                    startDecorator={getDetectionIcon(detectionResult.isLoadOffer)}
                                >
                                    <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                                        {(detectionResult.score * 100).toFixed(1)}% confidence
                                    </Typography>
                                </Chip>
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                    {detectionResult.isLoadOffer ? 'Load offer detected' : 'No load offer found'}
                                </Typography>
                            </Box>

                            {detectionResult.matches && detectionResult.matches.length > 0 ? (
                                <>
                                    <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                                        Detected Patterns ({detectionResult.matches.length})
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.75,
                                            maxHeight: 200,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {detectionResult.matches.map((match, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 'sm',
                                                    bgcolor: 'background.level1',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: 'primary.outlinedBorder',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        level="body-xs"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: 'text.primary',
                                                            fontFamily: 'monospace',
                                                            bgcolor: 'primary.softBg',
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 'xs',
                                                        }}
                                                    >
                                                        "{match.sequence.join(' ')}"
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Chip
                                                            size="sm"
                                                            variant="outlined"
                                                            color="primary"
                                                            sx={{
                                                                minHeight: '20px',
                                                                fontSize: '0.65rem',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {(match.score * 100).toFixed(0)}%
                                                        </Chip>
                                                        {match.weight && (
                                                            <Chip
                                                                size="sm"
                                                                variant="soft"
                                                                color="neutral"
                                                                sx={{
                                                                    minHeight: '20px',
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                W: {(match.weight * 100).toFixed(0)}%
                                                            </Chip>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 2,
                                        bgcolor: 'background.level1',
                                        borderRadius: 'sm',
                                        border: '1px dashed',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography level="body-sm" color="neutral">
                                        No specific patterns detected
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Popover>
                </Box>
            )}
        </Box>
    )
}
